import { createSelector, createSlice } from '@reduxjs/toolkit';

import { ESpotModel, FilterModel, HrefLangModel, TopCateg } from '@/models';
import { SlicePayload } from '@/types';
import { buildReducerError } from '@/utils/reducerUtils';
import { RootState } from '@/redux/create-store';

type CategStateStatusValue = { success: true, redirectUrl?: string } | { error: any } | Record<string, never>;
type CategStateStatusKey = 'categ' | 'categFilters';

export type CategStateStatus = { [key in CategStateStatusKey]?: CategStateStatusValue };

type CategContent = {
  products: any[];
  page?: number;
  sort?: string;
  searchTerm?: string;
  isCategViewWB?: boolean;
  filters?: string[];
  nbTotalRows?: number;
  top?: TopCateg[];
  pageTitle: string;
  sortOrder?: number;
  isSpirit: boolean;
  metaDescription: string;
  urlKeyword: string;
  hrefLang: HrefLangModel[];
  isPrivate: boolean;
  parentGroupIdentifier: string;
  image: string;
  ogImage: string;
  espotTopBanner: unknown;
  htmlDepartementPage: unknown;
  breadcrumbSeo: string[];
  headerEspots: Array<unknown>;
  footerEspots: Array<unknown>;
  isProducerCategory: boolean;
  espotListingPush: Record<string, unknown>;
  categLinks: string[];
  longDescription: string;
  htmlFooter: unknown;
}

type CategState = {
  categId?: string,
  currentlyAppliedFilters: Array<{label: string; filterString: string; nameId: string; valueId: string}>
  content: CategContent;
  espot?: ESpotModel;
  status: CategStateStatus;
  filters: string[],
  refreshData: boolean;
  filterQuery?: string;
}

export const initialState: CategState = {
  categId: undefined,
  currentlyAppliedFilters: [],
  content: { products: [] } as CategContent,
  espot: undefined,
  status: {},
  filters: [],
  refreshData: false,
  filterQuery: undefined
};

const categ = createSlice({
  name: 'categ',
  initialState,
  reducers: {
    setCategInfos: (
      state,
      action: SlicePayload<{
        categId: string, content: any, espot?: ESpotModel, filterQuery?: string, filters?: string[]
      }>
    ) => {
      const { categId, content, espot, filterQuery, filters } = action.payload;
      state.categId = categId;
      state.content = content;
      state.espot = espot;
      state.filterQuery = filterQuery;
      state.filters = filters ?? [];
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    updateCategFilters: () => {
    },

    applyFilters: (state, action: SlicePayload<CategState['currentlyAppliedFilters']>) => {
      state.currentlyAppliedFilters = action.payload;
    },

    updateCategFiltersSuccess: (state, action: SlicePayload<{ content: CategContent }>) => {
      state.status.categ = { success: true };
      state.content = {
        ...state.content,
        ...action.payload.content
      };
    },

    updateCategFiltersError: (state, action: SlicePayload<{ error: any }>) => {
      state.status.categ = buildReducerError(action.payload);
    },

    getCategWithFiltersAndProductsSuccess: (state, action: SlicePayload<{ content: CategContent }>) => {
      state.content = {
        ...state.content,
        ...action.payload.content
      };
    },

    getCategWithFiltersAndProductsError: (state, action: SlicePayload<{ error: any }>) => {
      state.status.categFilters = buildReducerError(action.payload);
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    getExclusiveCellarProducts: () => {
    },

    refreshCategData: (state) => {
      state.refreshData = true;
    },

    refreshCategDataClear: (state) => {
      state.refreshData = false;
    }
  }
});

export const {
  setCategInfos,
  updateCategFilters, updateCategFiltersSuccess, updateCategFiltersError,
  getCategWithFiltersAndProductsSuccess, getCategWithFiltersAndProductsError,
  getExclusiveCellarProducts, refreshCategData, refreshCategDataClear, applyFilters
} = categ.actions;

export default categ.reducer;

const categSlice = (state: RootState) => state.categ;
export const selectCateg = createSelector(
  [categSlice],
  (categ) => categ.content
);

export const selectCategEspots = createSelector(
  [categSlice],
  (categ) => categ.espot
);
